import type { TRequestComment, TRequestResponder } from '../../types';

// QA/demo fixtures for `?force=solicitud-detalle[:variant]`, read through the
// shared `useForcedUi` convention. OFF BY DEFAULT: with no URL parameter
// nothing here runs. Mirrors `hooks/use-my-requests/forced-my-requests.ts`.
//
// Why this exists: TIM-557 (the real detail + comments backend) does not
// exist yet (verified against the qa gateway, 2026-09-20). Every branch below
// is mock — `useRequestDetail` has no live counterpart to fall back to.

type TForcedRequestDetailResult = {
  respondedBy: TRequestResponder | null;
  comments: TRequestComment[];
  loading: boolean;
  error: boolean;
};

const KNOWN_VARIANTS = new Set(['', 'loading', 'error', 'paginated']);

const isKnownForcedRequestDetailVariant = (
  variant: string | undefined,
): boolean => variant !== undefined && KNOWN_VARIANTS.has(variant);

// --- Session-only comment store ---------------------------------------------
//
// No mutation exists for TIM-557 yet, so a submitted comment cannot reach a
// server. It lives here, keyed by request id, for the rest of the browser
// session only — the same "removable/appendable local state" shape as
// `forced-my-requests.ts`, subscribed through `useSyncExternalStore` so the
// thread re-renders when a comment is added.

let version = 0;
let listeners: Array<() => void> = [];

// TEMPORARY SCAFFOLDING — DELETE THIS FILE WITH TIM-557.
//
// This Map grows for the life of the tab: it has no size cap, no TTL and no
// eviction. That is deliberate and it is safe only because the map is
// unreachable without the `?force=solicitud-detalle` URL toggle, which is a
// QA and demo aid. A real user never writes to it.
//
// Do NOT copy this as a pattern for a store that production code can reach.
// When TIM-557 ships the detail and comment operations, this whole file goes
// away and the thread reads the server.
const addedComments = new Map<string, TRequestComment[]>();

export const addForcedRequestComment = (
  requestId: string,
  comment: TRequestComment,
): void => {
  const current = addedComments.get(requestId) ?? [];
  addedComments.set(requestId, [...current, comment]);
  version += 1;
  listeners.forEach(listener => listener());
};

export const subscribeToForcedRequestDetail = (
  listener: () => void,
): (() => void) => {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter(current => current !== listener);
  };
};

export const getForcedRequestDetailVersion = (): number => version;

// --- Fixture data ------------------------------------------------------------

const RESPONDER: TRequestResponder = {
  id: 'forced-responder-1',
  full_name: 'Marcela Rojas',
  profile_picture: { url: null },
};

const buildComment = (
  overrides: Partial<TRequestComment> & { id: string },
): TRequestComment => ({
  comment: 'Comentario de prueba.',
  created_at: '2026-09-05T09:00:00.000Z',
  user: {
    id: RESPONDER.id,
    full_name: RESPONDER.full_name,
    profile_picture: RESPONDER.profile_picture,
  },
  ...overrides,
});

// Bare `?force=solicitud-detalle` — 2 comments, oldest first (AC-4).
const BASE_COMMENTS: TRequestComment[] = [
  buildComment({
    id: 'forced-comment-1',
    comment:
      'Quedó aprobada la solicitud con el monto registrado en asistencia.',
    created_at: '2026-09-05T09:00:00.000Z',
  }),
  buildComment({
    id: 'forced-comment-2',
    comment: '¿Puedes confirmar si el ajuste ya se refleja en mi nómina?',
    created_at: '2026-09-05T14:30:00.000Z',
    user: {
      id: 'forced-collaborator-1',
      full_name: 'Camila Torres',
      profile_picture: { url: null },
    },
  }),
];

// `?force=solicitud-detalle:paginated` — 7 comments, 3 pages at 3-per-page.
const PAGINATED_COMMENT_COUNT = 7;

const PAGINATED_COMMENTS: TRequestComment[] = Array.from(
  { length: PAGINATED_COMMENT_COUNT },
  (_unused, index) =>
    buildComment({
      id: `forced-paginated-comment-${index + 1}`,
      comment: `Comentario número ${index + 1} sobre esta solicitud.`,
      created_at: `2026-09-0${(index % 9) + 1}T09:00:00.000Z`,
    }),
);

/**
 * Maps the `?force=solicitud-detalle[:variant]` token to a forced result.
 *
 * `undefined` in (no token, or a variant this map does not know) →
 * `undefined` out, so the caller keeps its default (empty) mock state. An
 * unknown variant falls through exactly like the absent param, on purpose.
 */
export const resolveForcedRequestDetail = (
  variant: string | undefined,
  requestId: string,
): TForcedRequestDetailResult | undefined => {
  if (!isKnownForcedRequestDetailVariant(variant)) {
    return undefined;
  }

  const added = addedComments.get(requestId) ?? [];

  if (variant === 'loading') {
    return { respondedBy: null, comments: [], loading: true, error: false };
  }
  if (variant === 'error') {
    return { respondedBy: null, comments: [], loading: false, error: true };
  }
  if (variant === 'paginated') {
    return {
      respondedBy: RESPONDER,
      comments: [...PAGINATED_COMMENTS, ...added],
      loading: false,
      error: false,
    };
  }

  return {
    respondedBy: RESPONDER,
    comments: [...BASE_COMMENTS, ...added],
    loading: false,
    error: false,
  };
};
